"use client"

import type { Breadcrumb, ClientEventType } from "./client-error-types"
import { sanitizeReportValue } from "./url-sanitizer"

const REPORT_URL =
  process.env.NEXT_PUBLIC_CLIENT_REPORT_URL || "/api/client-events"
const TRACE_ID_KEY = "__client_trace_id__"
const MAX_PAYLOAD_BYTES = 60_000
const MAX_BREADCRUMBS = 20
const MAX_STACK_LENGTH = 8_000
const MAX_STRING_LENGTH = 2_000

let traceIdCache: string | null = null
const breadcrumbs: Breadcrumb[] = []

const createId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }

  return `${Date.now()}_${Math.random().toString(36).slice(2)}`
}

export const getTraceId = () => {
  if (typeof window === "undefined") return "server"
  if (traceIdCache) return traceIdCache

  try {
    const existing = sessionStorage.getItem(TRACE_ID_KEY)
    if (existing) {
      traceIdCache = existing
      return existing
    }

    const id = createId()
    sessionStorage.setItem(TRACE_ID_KEY, id)
    traceIdCache = id
    return id
  } catch {
    traceIdCache = createId()
    return traceIdCache
  }
}

const getCurrentHref = () =>
  `${window.location.origin}${window.location.pathname}`

export const addClientBreadcrumb = (
  event: { type: string } & Record<string, unknown>,
) => {
  if (typeof window === "undefined") return

  breadcrumbs.push({
    ...event,
    time: new Date().toISOString(),
    href: getCurrentHref(),
  })

  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.splice(0, breadcrumbs.length - MAX_BREADCRUMBS)
  }
}

const getBreadcrumbs = () => breadcrumbs.slice()

const redact = (value: unknown): unknown =>
  sanitizeReportValue(value, {
    maxStringLength: MAX_STRING_LENGTH,
    maxStackLength: MAX_STACK_LENGTH,
  })

const safeStringify = (value: unknown) => {
  const seen = new WeakSet<object>()

  return JSON.stringify(redact(value), (_key, val) => {
    if (typeof val === "object" && val !== null) {
      if (seen.has(val)) return "[Circular]"
      seen.add(val)
    }

    if (typeof val === "bigint") return String(val)
    return val
  })
}

const getByteSize = (value: string) => {
  if (typeof Blob !== "undefined") {
    return new Blob([value]).size
  }

  return value.length
}

export const reportClientEvent = (type: ClientEventType, payload?: unknown) => {
  if (typeof window === "undefined") return

  void (async () => {
    try {
      const { buildClientEvent } = await import("./client-error-payload")
      const event = buildClientEvent({
        errorId: createId(),
        type,
        traceId: getTraceId(),
        breadcrumbs: getBreadcrumbs(),
        payload,
      })

      const body = safeStringify(event)
      if (getByteSize(body) > MAX_PAYLOAD_BYTES) return

      fetch(REPORT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        credentials: "same-origin",
        keepalive: true,
      }).catch(() => {
        // no-op: observability must not affect the app.
      })
    } catch {
      // no-op
    }
  })()
}
